import { useMcpConnector } from '../hooks/use-mcp-connectors';

export interface ConnectorErrorDetailsDrawerProps {
  connectorId: string;
  onClose: () => void;
}

/**
 * Sprint A4 S1 PR 3 — Task 15. Drawer com detalhes do último handshake error.
 *
 * Mostra code/message/occurredAt + guidance contextual baseado no código de
 * erro canonical (unreachable/invalid-response/business-error/version-mismatch).
 */
export function ConnectorErrorDetailsDrawer({
  connectorId,
  onClose,
}: ConnectorErrorDetailsDrawerProps): JSX.Element | null {
  const { data: connector } = useMcpConnector(connectorId);
  if (connector?.lastHandshakeError == null) return null;

  const err = connector.lastHandshakeError;
  const guidance = SUGGESTED_ACTION[err.code] ?? SUGGESTED_ACTION.default;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50">
      <div className="h-full w-full max-w-md overflow-y-auto bg-surface p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary">
            Detalhes do erro · {connector.displayName}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="text-text-secondary hover:text-text-primary"
          >
            ✕
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <p className="text-xs text-text-secondary">Código do erro</p>
            <code className="block rounded bg-bg-muted px-3 py-2 font-mono text-sm">
              {err.code}
            </code>
          </div>

          <div>
            <p className="text-xs text-text-secondary">Mensagem</p>
            <p className="rounded bg-bg-muted px-3 py-2 font-mono text-xs">{err.message}</p>
          </div>

          <div>
            <p className="text-xs text-text-secondary">Quando aconteceu</p>
            <p className="text-sm text-text-primary">
              {new Date(err.occurredAt).toLocaleString('pt-BR')}
            </p>
          </div>

          <div className="mt-4 rounded bg-blue-50 p-3 text-xs text-blue-900">
            <strong>Como diagnosticar:</strong> {guidance}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border-subtle px-4 py-2 text-sm hover:bg-bg-muted"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Guidance contextual por código de erro canonical. Default usado quando
 * código não bate (forward-compatible para novos códigos sem quebrar UI).
 */
const SUGGESTED_ACTION: Record<string, string> = {
  unreachable:
    'O servidor MCP não está respondendo. Verifique se a URL está correta + se o servidor do fornecedor está no ar (status page deles). Clique em "Testar de novo" no card após confirmar.',
  'invalid-response':
    'O servidor respondeu mas em formato inesperado (provavelmente versão incompatível). Confirme com o fornecedor a versão do contrato MCP suportado.',
  'business-error':
    'O servidor MCP retornou erro de negócio (credencial inválida, permissão insuficiente). Tente trocar a credencial ou validar o escopo de acesso com o fornecedor.',
  'version-mismatch':
    'O contrato do servidor MCP está em versão diferente da suportada pelo Lume. Atualize o servidor MCP do fornecedor ou aguarde release Lume com nova versão.',
  default:
    'Verifique se o servidor MCP do fornecedor está acessível, se a URL está correta e se a credencial não expirou. Clique em "Testar de novo" no card depois de corrigir.',
};
